/** 会社マスタ(グループ会社対応)。取引と口座はここで登録した会社に紐づく */
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { fetchCompanies, setCompanyActive, upsertCompany } from './companyRepo';
import type { Company } from '@core/lib/types';

function emptyCompany(order: number): Company {
  return {
    id: '',
    code: '',
    name: '',
    is_default: false,
    display_order: order,
    active: true,
  };
}

export default function CompaniesTab() {
  const toast = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [editing, setEditing] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCompanies(await fetchCompanies());
    } catch (e) {
      toast(e instanceof Error ? e.message : '会社マスタを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!editing) return;
    setBusy(true);
    try {
      await upsertCompany({ ...editing, id: editing.id.trim().toLowerCase() });
      await load();
      setEditing(null);
      toast('会社を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(company: Company) {
    try {
      await setCompanyActive(company.id, !company.active);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '更新に失敗しました');
    }
  }

  return (
    <>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          会社マスタ
          <button
            className="btn-line"
            style={{ marginLeft: 'auto' }}
            onClick={() => setEditing(emptyCompany((companies.length + 1) * 10))}
          >
            会社を追加
          </button>
        </h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          取引と口座は会社に紐づきます。入力時の初期値は「既定」の会社(SUM)です。
          社数が確定したら、不要な会社は削除せず「無効」にしてください。
        </p>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>コード</th>
                <th>会社名</th>
                <th>既定</th>
                <th className="num">表示順</th>
                <th>状態</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id}>
                  <td className="hint">{c.id}</td>
                  <td>{c.code}</td>
                  <td>{c.name}</td>
                  <td>{c.is_default && <span className="badge b-plan">既定</span>}</td>
                  <td className="num">{c.display_order}</td>
                  <td>
                    <span className={`badge ${c.active ? 'b-ok' : 'b-ng'}`}>
                      {c.active ? '有効' : '無効'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="txn-link" onClick={() => setEditing({ ...c })}>
                      編集
                    </button>
                    <button className="txn-link" onClick={() => void toggleActive(c)}>
                      {c.active ? '無効にする' : '有効にする'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>{companies.some((c) => c.id === editing.id) ? '会社を編集' : '会社を追加'}</h3>
          </div>
          <div className="form-grid">
            <div>
              <label>ID(英小文字。後から変更不可)</label>
              <input
                value={editing.id}
                disabled={companies.some((c) => c.id === editing.id)}
                onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                placeholder="例)sum"
              />
            </div>
            <div>
              <label>コード</label>
              <input
                value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                placeholder="例)SUM"
              />
            </div>
            <div>
              <label>会社名</label>
              <input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </div>
            <div>
              <label>入力時の初期値にする</label>
              <select
                value={editing.is_default ? '1' : '0'}
                onChange={(e) => setEditing({ ...editing, is_default: e.target.value === '1' })}
              >
                <option value="0">しない</option>
                <option value="1">する</option>
              </select>
            </div>
            <div>
              <label>表示順</label>
              <input
                type="number"
                value={editing.display_order}
                onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })}
              />
            </div>
            <div>
              <label>状態</label>
              <select
                value={editing.active ? '1' : '0'}
                onChange={(e) => setEditing({ ...editing, active: e.target.value === '1' })}
              >
                <option value="1">有効</option>
                <option value="0">無効</option>
              </select>
            </div>
          </div>
          <button className="btn-green" onClick={() => void save()} disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
        </Modal>
      )}
    </>
  );
}
